<template>
  <div class="v-file-uploader" :class="{ [this.extraClass]: true }">
    <div class="v-file-uploader__label-wrapper">
      <label if="label" class="label">
        {{ label }}
      </label>
      <VHint
        v-if="hintProps"
        v-bind="hintProps"
        extraClass="support-hint"
      ></VHint>
    </div>
    <div v-if="attachments.length > 0" class="v-file-uploader__thumbnails">
      <div class="item-wrapper" v-for="thumbnail in thumbnails">
        <div class="remove-btn" @click="onRemoveFile(thumbnail.key)">x</div>
        <img :src="thumbnail.url" alt="thumbnail" />
      </div>
    </div>
    <WithLoader :isLoading="_isLoading"></WithLoader>
    <VButton
      v-if="type === 'simple' || attachments.length > 0"
      type="gray"
      size="regular"
      inverted
      label="Upload image"
      extraClass="upload-btn right"
      :disabled="isLoading"
      @click="() => this.$refs.input.click()"
    >
      Click to upload image
    </VButton>
    <input
      type="file"
      accept="image/*"
      ref="input"
      multiple
      @change="
        evt => {
          onUpload(Array.from(evt.target.files || []));
        }
      "
      id="default-input"
    />

    <div v-show="shouldDragDropDisplayed" id="drop-area">
      <img :src="dragAndDropIcon" alt="dnd-icon" />
      <span>Drag and Drop HTML/PDF Here</span>
      <h1 id="or">OR</h1>
      <VButton
        type="gray"
        size="regular"
        inverted
        label="Add File"
        extraClass="upload-btn"
        @click="() => this.$refs.input.click()"
      ></VButton>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";

//components
import WithLoader from "@/components/WithLoader";
import VButton from "@/components/VButton";
import VHint from "@/components/VHint";

//mixins
import withToasted from "@/mixins/withToasted";

//assets
import dragAndDropIcon from "@/assets/images/dnd-icon.svg";

export default Vue.component("VSimpleFileUploader", {
  props: {
    type: VueTypes.oneOf(["simple", "dragAndDrop"]),
    label: VueTypes.string,
    isLoading: VueTypes.bool,
    withThumbnails: VueTypes.bool.def(true),
    withDelete: VueTypes.bool,
    onAddHandler: VueTypes.func,
    onRemoveHandler: VueTypes.func,
    extraClass: VueTypes.string,
    innerChildProps: VueTypes.object
  },

  components: { WithLoader, VButton, VHint },

  mixins: [withToasted],

  data() {
    return {
      attachments: [],
      thumbnails: [],
      dragAndDropIcon
    };
  },

  computed: {
    _isLoading() {
      return this.isLoading;
    },

    shouldDragDropDisplayed() {
      return (
        this.type === "dragAndDrop" &&
        this.attachments.length < 1 &&
        !this.isLoading
      );
    },

    hintProps() {
      return this.innerChildProps["hint"];
    }
  },

  methods: {
    async onUpload(files) {
      let unattachedFiles = [];
      let attachedFiles = [];

      //onAddHandler should return
      //array of names
      if (this.onAddHandler) {
        let uploadedFiles = await this.onAddHandler(files);
        //devide files to success and error parts
        files.forEach(file =>
          (uploadedFiles.includes(file.name)
            ? attachedFiles
            : unattachedFiles
          ).push(file)
        );
      } else {
        //if prop "onAddHandler" doen't exist
        //just attach files
        attachedFiles = files;
      }

      //push attached local state
      this.attachments.push(...attachedFiles);

      //thumbnails logic
      if (this.withThumbnails) {
        //add thumbnails

        for (const item of attachedFiles) {
          this.thumbnails.push({
            // key = file name
            key: item.name,
            url: URL.createObjectURL(item)
          });
        }
      }

      //tost logic
      if (attachedFiles.length > 0) {
        attachedFiles.forEach(file =>
          this.toast(`${file.name} attached succsessfully`)
        );
      }
      if (unattachedFiles.length > 0) {
        unattachedFiles.forEach(file =>
          this.toast(`${file.name} attached failed`, "error")
        );
      }

      return this.$emit("change", this.attachments);
    },

    async onRemoveFile(file_key) {
      let removedFromServer = "";
      if (this.onRemoveHandler) {
        try {
          //waiting for file name
          let response = await this.onRemoveHandler(file_key);
          removedFromServer = response;
        } catch {
          this.toast(`File ${file_key} not removed `, "error");
        }
      }

      let removedFileName = this.onRemoveHandler ? removedFromServer : file_key;

      this.attachments = this.attachments.filter(
        item => item.name !== removedFileName
      );
      this.$emit("change", this.attachments);
      this.thumbnails = this.thumbnails.filter(
        item => item.key !== removedFileName
      );
    },

    //drag&drop
    handleDrop(e) {
      let files = [...e.dataTransfer.files];
      this.onUpload(files);
    }
  },

  mounted() {
    //Vanila JS drag&drop
    let dropArea = document.getElementById("drop-area");

    function preventDefaults(e) {
      e.preventDefault();
      e.stopPropagation();
    }

    ["dragenter", "dragover", "dragleave", "drop"].forEach(eventName => {
      dropArea.addEventListener(eventName, preventDefaults, false);
      window.addEventListener(eventName, preventDefaults, false);
    });

    ["dragenter", "dragover"].forEach(eventName => {
      dropArea.addEventListener(eventName, highlight, false);
    });

    ["dragleave", "drop"].forEach(eventName => {
      dropArea.addEventListener(eventName, unhighlight, false);
    });

    function highlight() {
      dropArea.classList.add("highlight");
    }

    function unhighlight() {
      dropArea.classList.remove("highlight");
    }

    dropArea.addEventListener("drop", this.handleDrop, false);
  }
});
</script>

<style lang="scss" scoped>
$ns: "v-file-uploader";

.#{$ns} {
  display: grid;
}

#default-input {
  display: none;
}

.label {
  @include textCropEllipsis();
  font-size: 15px;
  color: $primary-text-color;
  padding-bottom: 14px;
  padding-left: 8px;
  vertical-align: middle;
}

.#{$ns}__label-wrapper {
  display: flex;
  align-items: baseline;
}

#drop-area {
  border: 1px dashed $primary-border-color;
  color: $tertiary-text-color;
  border-radius: 5px;
  height: 350px;
  width: 100%;
  display: grid;
  grid-template-rows: repeat(4, min-content);
  justify-content: center;
  justify-items: center;
  align-content: center;
  transition: background 0.2s linear;
  &.highlight {
    background: #f3f3f3;
  }

  #or {
    font-family: Lato-Bold, Arial, sans-serif;
    padding: 30px 0;
    color: $secondary-text-color;
  }
}

.#{$ns}__thumbnails {
  display: grid;
  padding-bottom: 30px;
  grid-gap: 20px;
  grid-template-columns: repeat(auto-fill, 140px);
  .item-wrapper {
    position: relative;
    width: 140px;
    text-align: end;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid;
    border-color: rgba(197, 197, 197, 0.3);
    border-radius: 3px;
  }

  .remove-btn {
    position: absolute;
    top: 0;
    right: 5px;
    width: fit-content;
    cursor: pointer;
    transition: color 0.2s linear;

    &:hover {
      color: $danger-color;
      font-family: Lato-Bold, Arial, sans-serif;
    }
  }

  img {
    height: 90px;
    width: 100%;
    object-fit: scale-down;
  }
}
.upload-btn {
  width: fit-content;

  &.right {
    margin-left: auto;
  }
}
</style>
<style lang="scss">
.support-hint {
  margin-left: 10px;
}
</style>
